import {
  ResponseInnerMessage,
  CbToken,
  ComponentMeta,
  FontFace,
  ActionInnerMessage,
  CbTokenResponse,
  EventMessage,
  tokenizationResponse,
  gatewayAdditionalInfo,
  BINData,
  GwTokenizationResponse,
  CbTokenizationAPIInput,
  TokenizationResponseAdditionalInfo,
  FieldStatus,
} from '@/hosted_fields/common/types';
import {Events, Field, HostedFields, Entity} from '@/hosted_fields/common/enums';
import {ComponentType} from '@/hosted_fields/common/base-types';
import Assert from '@/helpers/asserts';
import IframeClient, {HOST_NAME, MASTER_IFRAME_NAME} from '@/hosted_fields/common/connection/client';
import {Child, Host} from '@/hosted_fields/common/enums';
import Errors, {CbError} from '@/hosted_fields/common/errors';
import TokenizationHandler from '@/hosted_fields/master/tokenization-handler';
import t from '@/hosted_fields/common/locale';
import {find, has, clone, combineObjectArray, getValuesOf} from '@/hosted_fields/common/dom-utils';
import Sanitizer from '@/hosted_fields/common/sanitizer';
import Transformer from '@/helpers/transformer';
import DeviceDataHelpers from '@/helpers/device-data';
import '@/helpers/polyfills';
import ComponentsAndFieldsPluginInterface from '@/plugins/components_fields/actions/interface';
import {ApiClientInterface} from '@/plugins/core/api/interface';
import {PluginConfig} from '@/plugins/core/interface';
import ComponentsAndFieldsApis from '@/plugins/components_fields/api';
import CommManagerInterface from '@/hosted_fields/master/interface';
import {Master} from '@/hosted_fields/common/enums';
import {isValidURL, jsonify, safeGet} from '@/utils/utility-functions';
import {Address} from '@/plugins/three_domain_secure/types';
import Logger from '@/utils/logger_old';
import {Gateway, PaymentIntent, PaymentMethodType} from '@/extensions/three_domain_secure/common/types';
import EnvConstants from '@/constants/environment';

const helpers = {
  doFontWhitelisting: (fonts: FontFace[], whitelist: string[]) => {
    const fontsObj: {fonts: FontFace[]} = {
      fonts: <FontFace[]>clone(fonts),
    };
    // Sanitize & Whitelist fonts
    Sanitizer[HostedFields.Options.fonts](fontsObj.fonts, fontsObj, Entity.component, whitelist);
    return fontsObj.fonts;
  },
};

export default class ComponentsAndFieldsActions implements ComponentsAndFieldsPluginInterface {
  config: PluginConfig;
  private commMgr: CommManagerInterface;
  private iframeClient: IframeClient;
  private registeredComponents: Array<ComponentMeta> = [];
  private status = null;
  private apiClient: ApiClientInterface;
  private configuration: any;
  private loadConfig: Promise<any>;
  private tokenizationHandler: TokenizationHandler;
  init(): Promise<void> {
    this.apiClient.addApis(ComponentsAndFieldsApis);
    this.tokenizationHandler = new TokenizationHandler(this.commMgr);
    if (this.apiClient._config.publishableKey) {
      this.loadConfig = this.apiClient.config
        .retrieve()
        .then((config) => {
          this.configuration = config;
          return config;
        })
        .catch((error) => {
          const err = new CbError(error);
          Logger.error(err, {
            description: 'Error on retrieving tokenization config',
          });
          return Promise.reject(err);
        });
    } else {
      // TODO: when used with CB checkout, throwing error since publishable key is not set
      // this is required only for card components
      // this.loadConfig = Promise.reject(new CbError(t(Errors.missingPublishableKey)))
      this.loadConfig = Promise.resolve();
    }
    return Promise.resolve();
  }

  registerComponent(data: ComponentMeta) {
    const meta: ComponentMeta = {
      name: data.name,
      type: data.type,
      frames: [],
      options: data.options,
    };
    this.registeredComponents.push(meta);
    return Promise.resolve({registered: true});
  }

  registerField(data: any): Promise<ResponseInnerMessage> {
    const {componentName, fonts = []} = data;
    let component = this.findComponentByName(componentName);
    this.whitelistFonts({fonts, componentName});
    return this.iframeClient
      .sendMessage({action: Child.Actions.LoadComponent, data}, data.name, {timeout: 10000})
      .then(() => {
        // TODO: Promise chaining to be fixed after analayzing performance impacts
        if (data.type == ComponentType.Card) {
          this.loadConfig.then(() => {
            // Set font whitelist
            this.setWhitelistedFonts(data.name);
            const currency = safeGet(data, 'baseOptions.currency') || data.currencyCode;
            const gwConfig = this.getGwConfig('card', currency);
            this.sendToAll(
              {
                action: Child.Actions.sendConfigData,
                data: {gwConfig},
              },
              component
            );
            this.setTestCards({name: data.name, frame: data.frame}).catch((err) => {
              Logger.error(err, {
                description: 'Error setting test cards to host',
              });
            });
            if (!gwConfig) {
              const componentMeta: ComponentMeta = this.findComponentByName(componentName);
              const error = new CbError({
                name: 'gateway_config_unavailable',
                message: `Gateway configuration for currency: ${currency} unavailable`,
              });

              const payload: ActionInnerMessage = {
                action: Master.Actions.CaptureException,
                data: {
                  error: jsonify(error),
                  extraData: {
                    gateway_config: this.configuration && this.configuration.apm_config,
                    componentOptions: componentMeta.options,
                    description: 'Error loading gateway config',
                  },
                },
              };
              this.commMgr.invokeAction(payload);
              throw error;
            }

            this.tokenizationHandler.loadScript(gwConfig).catch((err) => {
              Logger.error(new CbError(err), {
                description: 'Error loading gateway config',
              });
              let error = new CbError(t('error.unknownError'));
              throw error;
            });
          });
        }
        component.frames.push(data.name);
        return {registered: true};
      });
  }

  getRegisteredComponents(): Array<ComponentMeta> {
    return this.registeredComponents;
  }

  public findComponentByName = (name: string): ComponentMeta => {
    return find(this.registeredComponents, (comp) => comp.name === name);
  };

  public findComponentByFrame = (frame: string): ComponentMeta => {
    return find(this.registeredComponents, (comp) => has(comp.frames, frame));
  };

  private getAllFrames = (): Array<string> => {
    return this.registeredComponents.reduce((allFrames, component) => allFrames.concat(component.frames), []);
  };

  whitelistFonts(data): Promise<ResponseInnerMessage> {
    const fonts: FontFace[] = data.fonts;
    const componentName: string = data.componentName;
    const component: ComponentMeta = this.findComponentByName(componentName);
    const whitelist = this.getAllowedFontDomains();

    const whitelistedFonts = this.loadConfig.then(() =>
      helpers.doFontWhitelisting(fonts, this.getAllowedFontDomains())
    );

    // Sanitize & Store fonts under component if not already present
    if (!(component.data && component.data.fonts)) {
      component.data = {
        ...component.data,
        fonts: whitelistedFonts,
      };
    } else {
      component.data.fonts = whitelistedFonts;
    }

    return Promise.all(component.frames.map((frameId) => this.setWhitelistedFonts(frameId)));
  }

  private callTokenConfigApi(currency: string, paymentIntent: PaymentIntent, resolve, reject) {
    this.fetchGatewayCredential(paymentIntent).then((resp) => {
      if (resp.pm_list && resp.pm_list[0]) {
        const gwConfig = resp.pm_list[0];
        this.configuration.apm_config[currency] = resp;
        this.tokenizationHandler
          .loadScript(gwConfig)
          .then(() => {
            resolve(gwConfig);
          })
          .catch((err) => {
            Logger.error(new CbError(err), {
              description: 'Error loading gateway config',
            });
            let error = new CbError(t('error.unknownError'));
            reject(error);
          });
      } else {
        resolve(null);
      }
    });
    const payload: ActionInnerMessage = {
      action: Master.Actions.CaptureKVL,
      data: {
        action: 'authorizeWith3ds_smart_routing_override',
        gw_id: paymentIntent.gateway_account_id,
        payment_intent_id: paymentIntent.id,
      },
    };
    this.commMgr.invokeAction(payload);
  }

  private getGwTokenizationConfig(pmType: string, currency?: string, paymentIntent?: PaymentIntent): any {
    return new Promise((resolve, reject) => {
      if (paymentIntent && paymentIntent.id) {
        // check if gateway is already present in apm_config
        const gwConfig = this.getGwConfig(pmType, currency);
        if (gwConfig.id === paymentIntent.gateway_account_id) {
          resolve(gwConfig);
        } else {
          /* if payment intent's gateway is not configured in smart routing for the currency,
              we will make api call to fetch tokenization config */
          this.callTokenConfigApi(currency, paymentIntent, resolve, reject);
        }
      } else {
        // for tokenize we will not have paymentIntent.
        resolve(this.getGwConfig(pmType, currency));
      }
    });
  }

  public getGwConfig(pmType: string, currency?: string): any {
    currency = currency || (this.configuration && this.configuration.default_currency);
    return (
      this.configuration &&
      this.configuration.apm_config &&
      this.configuration.apm_config[currency] &&
      this.configuration.apm_config[currency].pm_list &&
      this.configuration.apm_config[currency].pm_list.filter((i) => i.type == pmType)[0]
    );
  }

  getAllowedFontDomains(): string[] {
    try {
      return this.configuration && this.configuration.allowed_font_source_domains
        ? this.configuration.allowed_font_source_domains.split(',')
        : [];
    } catch (e) {
      return [];
    }
  }

  getCardDetails(componentName: string): Promise<any> {
    const component = this.findComponentByName(componentName);
    const frames = component.frames;
    return Promise.all(
      frames.map((frame) => {
        this.checkFrameRegistered(frame);
        return this.iframeClient.sendMessage({action: Child.Actions.FetchData}, frame);
      })
    );
  }

  getCardValuesFromDetails(details: any) {
    let isValid = true;
    const cardValues = combineObjectArray(details);

    // Check for field validity
    getValuesOf(cardValues).map((status) => {
      if (!status.isValid) isValid = false;
    });

    // Get only field values
    Object.keys(cardValues).map((fieldName) => {
      cardValues[fieldName] = cardValues[fieldName].value;
    });

    return cardValues;
  }

  _getCardType(type) {
    switch (type) {
      case 'dinersclub':
        return 'diners_club';
      case 'amex':
        return 'american_express';
      case 'cmrfalabella':
        return 'cmr_falabella';
      case 'tarjetanaranja':
        return 'tarjeta_naranja';
      default:
        return type;
    }
  }

  getCardTypeFromDetails(cardDetails: any) {
    for (let index = 0; index < cardDetails.length; index++) {
      const obj = cardDetails[index];
      if (obj && obj.cardnumber) {
        return this._getCardType(obj.cardnumber.cardType);
      }
    }
  }

  tokenizeCardData({currency, componentName, additionalData}): Promise<ResponseInnerMessage> {
    return this.loadConfig
      .then(() => {
        return this.getCardDetails(componentName);
      })
      .then(async (cardDetails) => {
        let cardValues = this.getCardValuesFromDetails(cardDetails);
        let paymentIntent = null;
        if (additionalData && additionalData.paymentIntent) paymentIntent = additionalData.paymentIntent;
        let gwConfig = await this.getGwTokenizationConfig('card', currency, paymentIntent).catch((err) => {
          Logger.error(err, {description: 'Error tokenizing card details'});
          return Promise.reject(err);
        });

        if (!gwConfig) {
          const componentMeta: ComponentMeta = this.findComponentByName(componentName);
          const error = new CbError({
            name: 'gateway_config_unavailable',
            message: `Gateway configuration for currency: ${currency} unavailable`,
          });
          const payload = {
            action: Master.Actions.CaptureException,
            data: {
              error: jsonify(error),
              extraData: {
                gateway_config: this.configuration && this.configuration.apm_config,
                componentOptions: componentMeta.options,
              },
            },
          };
          this.commMgr.invokeAction(payload);
          return Promise.reject(error);
        }
        // TODO: to handle in respective gateway handler to avoid pollution
        if (gwConfig.gateway_name.toLowerCase() === Gateway.CYBERSOURCE) {
          cardValues = Object.assign({}, cardValues, {
            cardType: this.getCardTypeFromDetails(cardDetails),
          });
        }
        if (gwConfig.tokenization_config) {
          return this.createTokenUsingThirdParty(gwConfig, {
            cardData: cardValues,
            additionalData: additionalData,
          });
        } else {
          return this.createTokenUsingChargebee(gwConfig, cardValues, additionalData);
        }
      })
      .catch((err) => {
        Logger.error(err, {description: 'Error tokenizing card details'});
        return Promise.reject(err);
      });
  }

  private checkFrameRegistered(frame) {
    if (frame == MASTER_IFRAME_NAME) return;
    Assert.notTrue(() => has(this.getAllFrames(), frame), t(Errors.componentNotRegistered));
  }

  tokenizeBankData({currency, bankData}): Promise<ResponseInnerMessage> {
    return this.loadConfig
      .then(() => {
        let gwConfig = this.getGwConfig('direct_debit', currency);
        return this.createTokenUsingThirdParty(gwConfig, {bankData: bankData});
      })
      .catch((err) => {
        Logger.error(err, {description: 'Error tokenizing bank details'});
        return Promise.reject(err);
      });
  }

  private chargebeeCardTransformer(cc, additionalData) {
    let exp = cc[Field.Expiry].split('/');
    let out = {
      'card[number]': cc[Field.Number].replace(/\s+/g, ''),
      'card[cvv]': cc[Field.CVV],
      'card[expiry_month]': exp[0],
      'card[expiry_year]': exp[1],
    };
    if (cc.cardType) {
      out['card[card_type]'] = cc.cardType;
    }
    if (additionalData) {
      const addr: Address = Transformer.billingAddress(additionalData);
      out['card[first_name]'] = addr.firstName;
      out['card[last_name]'] = addr.lastName;
      out['card[billing_addr1]'] = addr.addressLine1;
      out['card[billing_addr2]'] = addr.addressLine2;
      out['card[billing_city]'] = addr.city;
      out['card[billing_zip]'] = addr.zip;
      out['card[billing_country]'] = addr.countryCode;

      if (addr.stateCode) {
        out['card[billing_state_code]'] = addr.stateCode;
      } else {
        out['card[billing_state]'] = addr.state;
      }
      if (additionalData.additional_information && Object.keys(additionalData.additional_information).length > 0) {
        out['card[additional_information]'] = JSON.stringify(additionalData.additional_information);
      }
    }
    return Transformer.clean(out);
  }

  private getGatewayAdditionalInfo(gwid): Promise<gatewayAdditionalInfo> {
    return new Promise((resolve) => {
      if (this.apiClient._config && this.apiClient._config.publishableKey) {
        this.apiClient.config
          .retrieve_gateway_additional_info({}, {gw_id: gwid})
          .then((config) => {
            resolve(config);
          })
          .catch((error) => {
            const err = new CbError(error);
            Logger.error(err, {
              description: 'Error on retrieving gateway additional info',
            });
            resolve({});
          });
      } else {
        resolve({});
      }
    });
  }

  private attachDeviceData(gwConfig, additionalData) {
    return new Promise((resolve, reject) => {
      // Reference: https://mychargebee.atlassian.net/wiki/spaces/DevD/pages/1897041371/Additional+Card+and+Payment+Method+Params
      if (gwConfig.gateway_name.toLowerCase() === Gateway.BLUESNAP) {
        let kountMerchantId = '';
        this.getGatewayAdditionalInfo(gwConfig.id)
          .then((data) => {
            kountMerchantId = data.kount_merchant_id;
          })
          .finally(() => {
            let additional_information = additionalData && additionalData.email ? {email: additionalData.email} : {};
            DeviceDataHelpers.getBluesnapSessionId(this.commMgr.isTestSite(), kountMerchantId)
              .then((sessionId) => {
                additional_information['bluesnap'] = {
                  fraud: {fraud_session_id: sessionId},
                };
                resolve({
                  ...additionalData,
                  additional_information: additional_information,
                });
              })
              .catch((err) => {
                Logger.error(err);
                // we will bypass device data check
                // reject(err);
                resolve({
                  ...additionalData,
                  additional_information: additional_information,
                });
              });
          });
      } else {
        resolve(additionalData);
      }
    });
  }

  private createTokenUsingChargebee(gwConfig, cc, additionalData): Promise<CbToken> {
    return this.attachDeviceData(gwConfig, additionalData).then((additionalData: any) => {
      let data = this.chargebeeCardTransformer(cc, additionalData);
      data['card[gateway_account_id]'] = gwConfig.id;
      return this.apiClient.tokens.create_for_card({}, data).then((response: CbTokenResponse) => {
        const tokenObj = response.token;
        const tokenResponse: CbToken = {
          token: tokenObj.id,
          vaultToken: tokenObj.id_at_vault,
          additional_information: additionalData.additional_information,
        };
        return tokenResponse;
      });
    });
  }

  private addressTransformer(gwConfig, apipayload, additionalData) {
    if (
      (gwConfig.gateway_name.toLowerCase() === Gateway.AUTHORIZE_NET ||
        gwConfig.gateway_name.toLowerCase() === Gateway.EBANX) &&
      additionalData
    ) {
      const addr: Address = Transformer.billingAddress(additionalData);
      apipayload['token_billing_address[line1]'] = addr.addressLine1;
      apipayload['token_billing_address[line2]'] = addr.addressLine2;
      apipayload['token_billing_address[city]'] = addr.city;
      apipayload['token_billing_address[zip]'] = addr.zip;
      apipayload['token_billing_address[country_code]'] = addr.countryCode;

      if (addr.stateCode) {
        apipayload['token_billing_address[state_code]'] = addr.stateCode;
      } else {
        apipayload['token_billing_address[state]'] = addr.state;
      }

      apipayload['token_additional_detail[first_name]'] = addr.firstName;
      apipayload['token_additional_detail[last_name]'] = addr.lastName;
      apipayload = Transformer.clean(apipayload);
    }
  }

  private additionalInfoTransformer(gwConfig, apipayload, additionalData) {
    if (gwConfig.gateway_name.toLowerCase() === Gateway.EBANX && additionalData && additionalData['document']) {
      apipayload['additional_information'] = JSON.stringify({
        ebanx: {
          payer: {document: additionalData['document']['number']},
        },
      });
    }
  }

  private createTokenUsingThirdParty(gwConfig, payload): Promise<CbToken> {
    let additionalInformation: TokenizationResponseAdditionalInfo = null;
    return this.tokenizationHandler
      .tokenize(gwConfig, payload)
      .then((data: GwTokenizationResponse) => {
        const apipayload: CbTokenizationAPIInput = {
          gateway_account_id: gwConfig.id,
          payment_method_type: gwConfig.type,
          id_at_vault: data.token,
          gw_obj_type: 'token',
        };
        if (data.deviceData) {
          apipayload.additional_information = JSON.stringify(data.deviceData);
        }
        if (data.additional_information) {
          additionalInformation = data.additional_information;
        }
        this.addressTransformer(gwConfig, apipayload, payload.additionalData);
        this.additionalInfoTransformer(gwConfig, apipayload, payload.additionalData);

        return this.apiClient.tokens.create_using_temp_token({}, apipayload);
      })
      .then((response: CbTokenResponse) => {
        // Shimming unnecessary details
        const tokenObj = response.token;
        const tokenResponse: CbToken = {
          token: tokenObj.id,
          vaultToken: tokenObj.id_at_vault,
        };
        if (additionalInformation) {
          tokenResponse.additional_information = additionalInformation;
        }
        return tokenResponse;
      })
      .catch((err) => {
        Logger.error(err);
        return Promise.reject(err);
      });
  }

  relayEvent(data): Promise<ResponseInnerMessage> {
    this.checkFrameRegistered(data.frame);
    return this.iframeClient.sendMessage({action: Host.Actions.TriggerEvent, data}, HOST_NAME);
  }

  update(data): Promise<ResponseInnerMessage> {
    this.checkFrameRegistered(data.frame);
    return this.iframeClient.sendMessage({action: Child.Actions.Update, data}, data.frame);
  }

  /* To store all status changes */
  storeStatus(updatedStatus) {
    if (updatedStatus) {
      this.status = {
        ...this.status,
        ...updatedStatus,
      };
    }
  }

  getStatus() {
    return this.status;
  }

  private sanitizeBinData(binData: BINData): BINData {
    const outputBinData: BINData = binData ? {...binData} : {};
    // To ensure there are only 6 digits in the BIN number
    if (binData.bin) {
      outputBinData.bin = ((binData.bin || '') + '').slice(0, 6);
    }

    // To ensure there are only 4 digits in the last 4
    if (binData.last4) {
      outputBinData.last4 = ((binData.last4 || '') + '').slice(-4);
    }

    return outputBinData;
  }

  /**
   * Checks if a hostOrigin (full URL) is allowed based on the allowedDomains array,
   * which may include wildcards.
   *
   * @param {string} hostOrigin - The full URL to check.
   * @param {string[]} allowedDomains - An array of allowed domains, which may include wildcards.
   * @returns {boolean} Returns true if the hostOrigin is allowed, false otherwise.
   */
  isDomainAllowed(hostOrigin: string, allowedDomains: string[]): boolean {
    // Normalize the hostOrigin by extracting the hostname
    const url = new URL(hostOrigin);
    const normalizedOrigin = `${url.protocol}//${url.host}`.toLowerCase(); // Includes protocol, host, and port
    const normalizedHost = url.hostname.toLowerCase(); // Get the hostname (e.g., 'sub.example.com')

    for (const domain of allowedDomains) {
      // Normalize the allowed domain
      const normalizedDomain = domain.toLowerCase();

      if (normalizedOrigin === normalizedDomain) {
        return true; // Exact match found
      }

      // Handle wildcard domains manually (without new URL)
      if (normalizedDomain.startsWith('https://*') || normalizedDomain.startsWith('http://*')) {
        const baseDomain = normalizedDomain.replace(/^https?:\/\//, '').replace(/^\*\./, ''); // Remove protocol and wildcard

        // Ensure protocol matches
        const expectedProtocol = normalizedDomain.startsWith('https://') ? 'https:' : 'http:';
        if (url.protocol !== expectedProtocol) {
          continue;
        }

        // Check if the host matches the base domain or ends with the base domain
        if (normalizedHost === baseDomain || normalizedHost.endsWith(`.${baseDomain}`)) {
          return true; // Match found
        }
        continue;
      }

      // Try parsing the allowed domain if it is a valid URL (no wildcards)
      let domainUrl: URL | null = null;
      try {
        domainUrl = new URL(normalizedDomain.startsWith('http') ? normalizedDomain : `https://${normalizedDomain}`);
      } catch (err) {
        continue; // Skip invalid domains
      }

      if (domainUrl && url.protocol !== domainUrl.protocol) {
        continue; // Skip if protocol is different
      }

      // Exact domain match (without wildcards)
      if (normalizedOrigin === domainUrl.origin) {
        return true; // Now compares the full origin, including port
      }
    }
    return false; // No match found
  }

  async sanitizeData(data?: any) {
    // Data sanitization to check for card number exposure
    if (!data || typeof data !== 'object') return data;

    const cardNumberStatus: FieldStatus = data.status && data.status.cardnumber;
    let binData: BINData = cardNumberStatus && cardNumberStatus.binData;
    if (!binData) return data;

    // Domain where chargebee.js is initialized
    const windowURL = this.commMgr.site_meta.window_url;
    const jsInfo = await this.commMgr
      .getCheckoutUtilsPlugin()
      .then((checkoutUtilsPlugin) => checkoutUtilsPlugin.getInfo());
    const allowedDomains = jsInfo.allowed_domains || [];
    let hostName, hostOrigin;
    if (isValidURL(windowURL)) {
      const urlObj = new URL(windowURL);
      hostName = urlObj.hostname;
      hostOrigin = urlObj.origin;
    }

    const cbAppDomainUrl = new URL(EnvConstants.APP_DOMAIN.replace('${site}', this.commMgr.site));

    // To allow all chargebee domains (hosted pages) to access bin data
    // without adding domain allowed list
    const isAppDomain = cbAppDomainUrl.hostname.includes(hostName);
    // To check if its a valid URL whitelisted under add domains
    const isUrlAllowed = this.isDomainAllowed(hostOrigin, allowedDomains);
    const allowBinData = cardNumberStatus.isValid && cardNumberStatus.isComplete && (isAppDomain || isUrlAllowed);

    if (allowBinData) {
      binData = this.sanitizeBinData(binData);
      cardNumberStatus.binData = binData;
    } else {
      delete data.status.cardnumber.binData;
    }

    return data;
  }

  /* Method to broadcase status changes of all elements to IFRAMES & HOST */
  broadcast(data: EventMessage): Promise<ResponseInnerMessage> {
    this.checkFrameRegistered(data.frame);

    // Store status on 'change' Event
    if (data.event === Events.change) this.storeStatus(data.status);
    const component = this.findComponentByFrame(data.frame);
    const message = {action: Child.Actions.ReceiveStatusBroadcast, data};
    if (!component) return Promise.resolve(true);

    return this.sendToAll(message, component)
      .then(() => this.sanitizeData(data))
      .then((payload) =>
        this.iframeClient.sendMessage(
          {
            action: Host.Actions.ReceiveStatusBroadcast,
            data: payload,
          },
          HOST_NAME
        )
      );
  }

  // Send message to all frames registered under the component
  private sendToAll(message: ActionInnerMessage, component: ComponentMeta) {
    return Promise.all(component.frames.map((frameId) => this.iframeClient.sendMessage(message, frameId)));
  }

  focus(data): Promise<ResponseInnerMessage> {
    this.checkFrameRegistered(data.frame);
    return this.iframeClient.sendMessage({action: Child.Actions.Focus, data}, data.frame);
  }

  blur(data): Promise<ResponseInnerMessage> {
    this.checkFrameRegistered(data.frame);
    return this.iframeClient.sendMessage({action: Child.Actions.Blur, data}, data.frame);
  }

  clear(data): Promise<ResponseInnerMessage> {
    this.checkFrameRegistered(data.frame);
    return this.iframeClient.sendMessage({action: Child.Actions.Clear, data}, data.frame);
  }

  destroy(data): Promise<ResponseInnerMessage> {
    this.checkFrameRegistered(data.frame);
    return this.iframeClient.sendMessage({action: Child.Actions.Destroy, data}, data.frame);
  }

  validate(data): Promise<ResponseInnerMessage> {
    const component = this.findComponentByName(data.componentName);
    const frames = component.frames;
    return Promise.all(
      frames.map((iframeID) => this.iframeClient.sendMessage({action: Child.Actions.Validate, data}, iframeID))
    );
  }

  private setWhitelistedFonts(frameId): Promise<ResponseInnerMessage> {
    this.checkFrameRegistered(frameId);
    const component: ComponentMeta = this.findComponentByFrame(frameId);
    const whitelist = this.getAllowedFontDomains();
    return component.data.fonts.then((sanitizedFonts) =>
      this.iframeClient.sendMessage(
        {
          action: Child.Actions.UpdateFonts,
          data: {
            whitelist,
            fonts: sanitizedFonts,
          },
        },
        frameId
      )
    );
  }

  updateStyles(data): Promise<ResponseInnerMessage> {
    const message = {
      action: Child.Actions.UpdateStyles,
      data: data.style,
    };
    switch (data.entity) {
      case Entity.component: {
        const component: ComponentMeta = this.findComponentByName(data.componentName);
        return this.sendToAll(message, component);
      }
      case Entity.field: {
        this.checkFrameRegistered(data.frameId);
        return this.iframeClient.sendMessage(message, data.frameId);
      }
    }
  }

  updatePlaceholder(data): Promise<ResponseInnerMessage> {
    const message: ActionInnerMessage = {
      action: Child.Actions.UpdatePlaceholder,
      data: data.placeholder,
    };
    switch (data.entity) {
      default:
      case Entity.component: {
        const component: ComponentMeta = this.findComponentByName(data.componentName);
        return this.sendToAll(message, component);
      }
      case Entity.field: {
        this.checkFrameRegistered(data.frameId);
        return this.iframeClient.sendMessage(message, data.frameId);
      }
    }
  }

  updateAriaLabel(data): Promise<ResponseInnerMessage> {
    const message: ActionInnerMessage = {
      action: Child.Actions.UpdateAriaLabel,
      data: data.ariaLabel,
    };
    switch (data.entity) {
      default:
      case Entity.component: {
        const component: ComponentMeta = this.findComponentByName(data.componentName);
        return this.sendToAll(message, component);
      }
      case Entity.field: {
        this.checkFrameRegistered(data.frameId);
        return this.iframeClient.sendMessage(message, data.frameId);
      }
    }
  }

  updateIcon(data): Promise<ResponseInnerMessage> {
    const message: ActionInnerMessage = {
      action: Child.Actions.UpdateIcon,
      data: data.icon,
    };
    switch (data.entity) {
      default:
      case Entity.component: {
        const component: ComponentMeta = this.findComponentByName(data.componentName);
        return this.sendToAll(message, component);
      }
      case Entity.field: {
        this.checkFrameRegistered(data.frameId);
        return this.iframeClient.sendMessage(message, data.frameId);
      }
    }
  }

  updateLocale(data): Promise<ResponseInnerMessage> {
    const message: ActionInnerMessage = {
      action: Child.Actions.UpdateLocale,
      data: data.locale,
    };
    switch (data.entity) {
      default:
      case Entity.component: {
        const component: ComponentMeta = this.findComponentByName(data.componentName);
        return this.sendToAll(message, component);
      }
      case Entity.field: {
        this.checkFrameRegistered(data.frameId);
        return this.iframeClient.sendMessage(message, data.frameId);
      }
    }
  }

  iDealFocus(data): Promise<ResponseInnerMessage> {
    this.checkFrameRegistered(data.frame);
    const iDealComp = this.findComponentByFrame(data.frame);
    const targetFrameId = iDealComp.frames.find((frameId) => !!~frameId.indexOf('bank-list'));
    return this.iframeClient.sendMessage(
      {
        action: Host.Actions.ShowFrame,
        data: targetFrameId,
      },
      HOST_NAME
    );
  }

  iDealBlur(data): Promise<ResponseInnerMessage> {
    this.checkFrameRegistered(data.frame);
    const iDealComp = this.findComponentByFrame(data.frame);
    const targetFrameId = iDealComp.frames.find((frameId) => !!~frameId.indexOf('bank-list'));
    return this.iframeClient.sendMessage(
      {
        action: Host.Actions.HideFrame,
        data: targetFrameId,
      },
      HOST_NAME
    );
  }

  updateIDealBankList(data): Promise<ResponseInnerMessage> {
    // TODO: to flush destroyed components from registeredComponents
    const iDealComponents = this.registeredComponents.filter((comp) => comp.type === 'ideal');
    if (iDealComponents.length > 0) {
      const targetComponent = iDealComponents[iDealComponents.length - 1];
      const targetFrameId = targetComponent.frames.find((frameId) => !!~frameId.indexOf('bank-list'));
      if (targetFrameId) {
        const message: ActionInnerMessage = {
          action: Child.Actions.UpdateIDealBankList,
          data,
        };
        return this.iframeClient.sendMessage(message, targetFrameId);
      }
    }
  }

  iDealBankSelected(data): Promise<ResponseInnerMessage> {
    const iDealComponents = this.registeredComponents.filter((comp) => comp.type === 'ideal');
    if (iDealComponents.length > 0) {
      const targetComponent = iDealComponents[iDealComponents.length - 1];
      const targetFrameId = targetComponent.frames.find((frameId) => !!~frameId.indexOf('bank-select'));
      if (targetFrameId) {
        const message: ActionInnerMessage = {
          action: Child.Actions.IDealBankSelected,
          data: data.payload,
        };
        return this.iframeClient.sendMessage(message, targetFrameId);
      }
    }
  }

  constructor(commMgr: CommManagerInterface) {
    this.commMgr = commMgr;
    this.apiClient = commMgr.apiClient;
    this.iframeClient = commMgr.connectionClient;
  }

  fetchGatewayCredential(data: any): Promise<tokenizationResponse> {
    return this.apiClient.gw_payment_intent.get_gw_payment_method_config(
      {paymentIntentId: data.id},
      {},
      {headers: {Authorization: `Bearer ${data.id}`}}
    );
  }
  setTestCards(data: {name: string; frame: string}): Promise<ResponseInnerMessage> {
    const component = this.findComponentByFrame(data.frame);
    if (!this.commMgr.isTestSite() || (component.options && !component.options.showTestCards)) {
      return Promise.resolve(false);
    }
    const currency: string = this.getCurrency(component);
    const {test_cards: testCards} = this.getGwConfig('card', currency) || {};
    return this.iframeClient.sendMessage(
      {
        action: Host.Actions.setTestCards,
        data: {
          frame: data.frame,
          data: {testCards},
          event: Events.setTestCards,
        },
      },
      HOST_NAME
    );
  }
  selectTestCard(data: {index: string; frame: string}): Promise<ResponseInnerMessage> {
    const component = this.findComponentByFrame(data.frame);
    const currency: string = this.getCurrency(component);
    const {test_cards: testCards} = this.getGwConfig('card', currency) || {};
    const {index} = data || {};
    if (!index || !testCards[index]) {
      Logger.error('Unable to select test card', {
        description: 'Unable to select test card',
      });
      return Promise.reject('Unable to select test card');
    }
    const selectedCardData = testCards[index];
    return this.sendToAll(
      {
        action: Child.Actions.selectTestCard,
        data: {frame: data.frame, selectedCardData},
      },
      component
    );
  }
  getCurrency(component: ComponentMeta): string {
    return (component.options && component.options.currency) || this.configuration.default_currency;
  }
}
